import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, CornerUpLeft, CornerUpRight, ArrowUpLeft, ArrowUpRight, RotateCw, Flag, Undo2, Navigation, Phone, Gauge, ChevronDown } from "lucide-react";
import { MapPicker } from "@/components/MapPicker";
import { StatusBadge } from "@/components/StatusBadge";
import { api, errMsg, kina } from "@/lib/api";
import { googleNavUrl, kmBetween } from "@/lib/useDriverGps";
import { useRoute } from "@/lib/useRoute";

const ICONS = { left: CornerUpLeft, right: CornerUpRight, "sharp-left": CornerUpLeft, "sharp-right": CornerUpRight, "slight-left": ArrowUpLeft, "slight-right": ArrowUpRight, roundabout: RotateCw, arrive: Flag, uturn: Undo2 };
const NEXT = {
  accepted: ["arrived", "I've arrived at pickup"],
  arrived: ["in_progress", "Start trip · Start GPS meter"],
  in_progress: ["completed", "Complete trip · Cash received"],
};
const fmtM = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.max(10, Math.round(m / 10) * 10)} m`);

const NavBanner = ({ route, pos }) => {
  const steps = route?.steps || [];
  const step = steps[1] || steps[0];
  if (!step) return null;
  const Icon = ICONS[step.icon] || ArrowUp;
  const dist = pos ? (kmBetween(pos, { lat: step.location[0], lng: step.location[1] }) / 1.3) * 1000 : step.distance_m;
  const then = steps[2];
  return (
    <div data-testid="nav-banner" className="overflow-hidden rounded-2xl bg-[#0E5E57] text-white shadow-xl">
      <div className="flex items-center gap-4 p-4">
        <Icon className="h-10 w-10 shrink-0" />
        <div className="min-w-0">
          <p className="font-mono-num text-sm text-emerald-100">{fmtM(dist)}</p>
          <p className="truncate text-xl font-semibold" data-testid="nav-next-instruction">{step.instruction}</p>
        </div>
      </div>
      {then && <p className="truncate bg-black/20 px-4 py-1.5 text-xs text-emerald-100">Then: {then.instruction}</p>}
    </div>
  );
};

const StepsList = ({ steps }) => {
  const [open, setOpen] = useState(false);
  if (!steps?.length) return null;
  return (
    <div className="rounded-2xl border border-blue-100 bg-white">
      <button data-testid="nav-steps-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700">
        All directions ({steps.length}) <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ol className="max-h-64 space-y-1 overflow-y-auto px-4 pb-3 text-sm" data-testid="nav-steps-list">
          {steps.map((s, i) => (
            <li key={i} className="flex justify-between gap-3 border-t border-slate-100 py-1.5">
              <span className="text-slate-700">{s.instruction}</span>
              <span className="shrink-0 font-mono-num text-xs text-slate-400">{fmtM(s.distance_m)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const useDropoffAlert = (trip) => {
  const seen = useRef(trip.points_updated_at);
  useEffect(() => {
    if (trip.points_updated_at && trip.points_updated_at !== seen.current) {
      toast.info(`Passenger updated the route · now heading to ${trip.status === "in_progress" ? trip.dropoff.address : trip.pickup.address}`);
      seen.current = trip.points_updated_at;
    }
  }, [trip.points_updated_at, trip.status, trip.dropoff.address, trip.pickup.address]);
};

export const NavTrip = ({ trip, onUpdate, gps, GpsStatus }) => {
  const [next, label] = NEXT[trip.status];
  const target = trip.status === "in_progress" ? trip.dropoff : trip.pickup;
  const from = gps.pos || (trip.status === "in_progress" ? trip.pickup : null);
  const route = useRoute(from ? [from, target] : [trip.pickup, trip.dropoff], true);
  useDropoffAlert(trip);
  const advance = async () => {
    try {
      const { data } = await api.post(`/bookings/${trip.id}/status`, { status: next });
      toast.success(next === "completed" ? `Trip complete — collect ${kina(data.fare)} cash (${data.fare_basis === "gps" ? "GPS metered" : "estimate"})` : "Trip updated");
      onUpdate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const eta = route ? new Date(Date.now() + route.duration_min * 60000).toLocaleTimeString("en-AU", { timeZone: "Pacific/Port_Moresby", hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid="driver-active-trip">
      <div className="space-y-3 lg:col-span-7">
        <NavBanner route={route} pos={gps.pos} />
        <MapPicker pickup={trip.pickup} dropoff={trip.dropoff} taxi={gps.pos} passenger={trip.customer_location} route={route?.geometry} follow={Boolean(gps.pos)} fitKey={trip.status} className="h-[360px] sm:h-[480px]" />
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
          <p className="text-sm" data-testid="nav-eta">
            <b className="text-lg text-emerald-700">{route ? `${route.duration_min} min` : "—"}</b>
            <span className="ml-2 text-slate-500">{route ? `${route.distance_km} km · arrive ${eta}` : "calculating route…"}</span>
          </p>
          <a data-testid="open-google-maps-button" href={googleNavUrl(target)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600">
            <Navigation className="h-4 w-4" /> Navigate in Google Maps
          </a>
        </div>
        <GpsStatus gps={gps} />
        <StepsList steps={route?.steps} />
      </div>
      <div className="space-y-4 lg:col-span-5">
        <div className="rounded-2xl bg-[#0B192C] p-6 text-white">
          <div className="flex items-center justify-between">
            <StatusBadge status={trip.status} testId="driver-trip-status" />
            <span className="font-mono-num text-2xl font-semibold">{kina(trip.fare)}</span>
          </div>
          {trip.status === "in_progress" && (
            <p data-testid="driver-gps-meter" className="mt-3 flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm"><Gauge className="h-4 w-4 text-amber-400" /> GPS meter: <b className="font-mono-num">{(trip.tracked_km || 0).toFixed(2)} km</b></p>
          )}
          <p className="mt-5 text-xs font-bold uppercase tracking-widest text-blue-300">Passenger</p>
          <p className="mt-1 flex items-center justify-between font-semibold">
            {trip.customer_name}
            {trip.customer_phone && <a href={`tel:${trip.customer_phone}`} className="rounded-full bg-white/10 p-2" data-testid="call-passenger-button"><Phone className="h-4 w-4" /></a>}
          </p>
          {trip.customer_location && trip.status !== "in_progress" && <p className="mt-1 text-xs text-emerald-300">● Passenger is sharing live location</p>}
          <p className="mt-4 text-sm text-blue-100"><b className="text-amber-400">A</b> {trip.pickup.address}</p>
          <p className="mt-1 text-sm text-blue-100"><b className="text-amber-400">B</b> {trip.dropoff.address}</p>
          {trip.notes && <p className="mt-3 rounded-lg bg-white/10 p-3 text-sm">"{trip.notes}"</p>}
          <button data-testid="driver-advance-trip-button" onClick={advance} className="mt-6 h-12 w-full rounded-xl bg-amber-400 font-bold text-slate-900 transition-all hover:bg-amber-300 active:scale-[0.98]">{label}</button>
        </div>
      </div>
    </div>
  );
};
